import React from "react";
import useTerms from "../../../hooks/useTerms";
import { useClinicModule } from "../../../hooks/useClinicModule";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Avatar,
  Skeleton,
  Tooltip,
  LinearProgress,
  alpha,
} from "@mui/material";
import { FaUserInjured } from "react-icons/fa";
import { DataTableCard } from "../../../components/organisms";
import { formatPhoneDisplay } from "../../../components/atoms/PhoneInput";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import {
  fullName,
  initialsOf,
  avatarTone,
  ageOf,
  maskTc,
  relativePast,
  upcomingLabel,
  isSoon,
  fullDateTime,
  measurementSummary,
} from "./patientListFormat";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const COLS_ALL = 6;

const headSx = {
  fontWeight: 600,
  fontSize: TYPO.caption,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: COLORS.slate500,
  bgcolor: COLORS.white,
  borderBottom: `1px solid ${COLORS.slate200}`,
  py: 1.25,
  whiteSpace: "nowrap",
};

const cellSx = { py: 1.25, borderBottom: `1px solid ${COLORS.slate100}` };
const mainText = { fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800, lineHeight: 1.35 };
const subText = { fontSize: TYPO.caption, color: COLORS.slate500, lineHeight: 1.35 };
const dash = <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>;

/** Sıralanabilir başlık hücresi. */
function SortHead({ field, label, sort, dir, onSort, sx }) {
  const active = sort === field;
  return (
    <TableCell sx={{ ...headSx, ...sx }} sortDirection={active ? dir : false}>
      <TableSortLabel
        active={active}
        direction={active ? dir : field === "lastVisit" ? "desc" : "asc"}
        onClick={() => onSort(field)}
        sx={{ "&.Mui-active": { color: COLORS.slate800 }, "& .MuiTableSortLabel-icon": { fontSize: 16 } }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

const STATUS_TONE = { high: COLORS.errorDark, low: COLORS.warningDark };

export default function PatientsTable({
  items,
  totalCount,
  isLoading,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onOpenDetail,
  hasFilters,
  empty,
  sort,
  dir,
  onSort,
}) {
  const { palette } = useClinicTheme();
  const terms = useTerms();
  const showTreatment = useClinicModule("treatment");
  const COLS = showTreatment ? COLS_ALL : COLS_ALL - 1;

  return (
    <DataTableCard
      loading={Boolean(isLoading)}
      variant="flat"
      empty={!isLoading && items.length === 0}
      emptyIcon={<FaUserInjured />}
      emptyTitle={empty?.title || (hasFilters ? `${terms.patient} bulunamadı` : `Henüz ${terms.patientLower} yok`)}
      emptyMessage={empty?.message}
      emptyAction={empty?.action}
      page={page}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    >
      <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 1100 }}>
        <TableHead>
          <TableRow>
            <SortHead field="name" label={terms.patient} sort={sort} dir={dir} onSort={onSort} sx={{ pl: { xs: 2, md: 4 }, width: showTreatment ? "24%" : "34%" }} />
            <TableCell sx={{ ...headSx, width: "14%" }}>Telefon</TableCell>
            <SortHead field="lastVisit" label="Son ziyaret" sort={sort} dir={dir} onSort={onSort} sx={{ width: "11%" }} />
            <SortHead field="nextAppointment" label="Sıradaki randevu" sort={sort} dir={dir} onSort={onSort} sx={{ width: "13%" }} />
            {showTreatment && <TableCell sx={{ ...headSx, width: "22%" }}>Aktif tedavi</TableCell>}
            <TableCell sx={{ ...headSx, width: showTreatment ? "16%" : "28%", pr: { xs: 2, md: 4 } }}>Son ölçüm</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            [...Array(Math.min(10, rowsPerPage))].map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {Array.from({ length: COLS }).map((__, c) => (
                  <TableCell key={c} sx={{ ...cellSx, ...(c === 0 ? { pl: { xs: 2, md: 4 } } : {}) }}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : items.length ? (
            items.map((p) => <PatientRow key={p._id} patient={p} palette={palette} onOpenDetail={onOpenDetail} showTreatment={showTreatment} />)
          ) : (
            <TableRow>
              <TableCell colSpan={COLS} sx={{ height: 1, py: 0, border: 0 }} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </DataTableCard>
  );
}

function PatientRow({ patient, palette, onOpenDetail, showTreatment }) {
  const ins = patient.insights || {};
  const tone = avatarTone(patient);
  const age = ageOf(patient.birthDay);
  const meta = [age != null ? `${age} yaş` : null, patient.gender, patient.tcNo ? `TC ${maskTc(patient.tcNo)}` : null]
    .filter(Boolean)
    .join(" · ");
  const last = relativePast(ins.lastVisitAt);
  const next = upcomingLabel(ins.nextAppointmentAt);
  const tr = ins.activeTreatment;
  const m = ins.lastMeasurement;
  const ms = measurementSummary(m);
  const mTone = STATUS_TONE[m?.status];

  return (
    <TableRow
      hover
      onClick={() => onOpenDetail(patient)}
      sx={{ cursor: "pointer", "&:hover": { bgcolor: `${alpha(palette.primary, 0.03)} !important` } }}
    >
      {/* Hasta */}
      <TableCell sx={{ ...cellSx, pl: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar sx={{ width: 34, height: 34, fontSize: 12.5, fontWeight: 600, bgcolor: alpha(tone, 0.14), color: tone }}>
            {initialsOf(patient)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...mainText, fontWeight: 600 }} noWrap>
              {fullName(patient)}
            </Typography>
            {meta ? (
              <Typography sx={subText} noWrap>
                {meta}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>

      {/* Telefon */}
      <TableCell sx={cellSx}>
        <Typography sx={{ ...mainText, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {formatPhoneDisplay(patient.tellNo) || "—"}
        </Typography>
      </TableCell>

      {/* Son ziyaret */}
      <TableCell sx={cellSx}>
        {last ? (
          <Tooltip title={fullDateTime(ins.lastVisitAt)} arrow placement="top">
            <Typography sx={{ ...mainText, display: "inline-block" }}>{last}</Typography>
          </Tooltip>
        ) : (
          <Typography sx={subText}>Henüz gelmedi</Typography>
        )}
      </TableCell>

      {/* Sıradaki randevu */}
      <TableCell sx={cellSx}>
        {next ? (
          <Tooltip title={fullDateTime(ins.nextAppointmentAt)} arrow placement="top">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.25,
                borderRadius: 1.5,
                fontSize: TYPO.label,
                fontWeight: 600,
                whiteSpace: "nowrap",
                ...(isSoon(ins.nextAppointmentAt)
                  ? { bgcolor: alpha(palette.primary, 0.1), color: palette.primary }
                  : { bgcolor: COLORS.slate100, color: COLORS.slate700 }),
              }}
            >
              {next}
            </Box>
          </Tooltip>
        ) : (
          dash
        )}
      </TableCell>

      {/* Aktif tedavi — tedavi modülü kapalıysa sütun yok */}
      {showTreatment && (
      <TableCell sx={cellSx}>
        {tr ? (
          <Box sx={{ minWidth: 0, pr: 2 }}>
            <Tooltip title={tr.name} arrow placement="top">
              <Typography sx={mainText} noWrap>
                {tr.name}
              </Typography>
            </Tooltip>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (tr.month / tr.duration) * 100)}
                sx={{
                  flex: 1,
                  maxWidth: 90,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: COLORS.slate100,
                  "& .MuiLinearProgress-bar": { bgcolor: palette.primary, borderRadius: 2 },
                }}
              />
              <Typography sx={{ ...subText, whiteSpace: "nowrap" }}>
                {tr.month}/{tr.duration}. ay
              </Typography>
            </Box>
          </Box>
        ) : (
          dash
        )}
      </TableCell>
      )}

      {/* Son ölçüm */}
      <TableCell sx={{ ...cellSx, pr: { xs: 2, md: 4 } }}>
        {ms ? (
          <Tooltip
            title={`${fullDateTime(m.takenAt)}${m.status === "high" ? " · Yüksek" : m.status === "low" ? " · Düşük" : ""}`}
            arrow
            placement="top"
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {mTone ? <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: mTone, flexShrink: 0 }} /> : null}
                <Typography
                  sx={{ ...mainText, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", ...(mTone ? { color: mTone, fontWeight: 600 } : {}) }}
                >
                  {ms.label} {ms.value}
                </Typography>
              </Box>
              <Typography sx={subText}>{relativePast(m.takenAt)}</Typography>
            </Box>
          </Tooltip>
        ) : (
          dash
        )}
      </TableCell>
    </TableRow>
  );
}
